import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ColumnDef } from '../boomer-table.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Moment } from 'moment/moment';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import * as moment from 'moment';

export type FilterType =
  | { __type: 'text'; value: string }
  | { __type: 'date'; from: Moment; to: Moment }
  | { __type: 'select'; value: string }
  | { __type: 'multi-select'; values: Array<string> }
  | { __type: 'agent'; value: string }
  | { __type: 'boomer_user'; value: string };

@Component({
  selector: 'app-filter-menu',
  templateUrl: './filter-menu.component.html',
  styleUrls: ['./filter-menu.component.scss'],
})
export class FilterMenuComponent<T extends object> implements OnInit {
  @Input()
  column: ColumnDef<T>;

  @Output()
  filter = new EventEmitter<FilterType | null>();

  form: UntypedFormGroup;

  @ViewChild('menuTrigger')
  trigger: MatMenuTrigger;

  @ViewChild('menu')
  menu: MatMenu;

  get hasValue() {
    switch (this.column.type) {
      case 'exact_datetime':
      case 'date':
        return this.form.value.from !== null && this.form.value.to !== null;
      case 'number':
      case 'text':
      case 'agent':
      case 'boomer_user':
      case 'select':
        return this.form.value.value !== null;
      case 'multi-select':
        return Array.isArray(this.form.value.value) && this.form.value.value.length > 0;
    }
  }

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    if (!this.column.isFilterable) {
      throw new Error(`Column(${this.column.name}) is not filterable`);
    }

    switch (this.column.type) {
      case 'select':
      case 'agent':
      case 'boomer_user':
      case 'number':
      case 'text':
        this.form = this.formBuilder.group({
          value: null,
        });
        break;
      case 'multi-select':
        this.form = this.formBuilder.group({
          value: [[]],
        });
        break;
      case 'date':
      case 'exact_datetime':
        this.form = this.formBuilder.group({
          from: null,
          to: null,
        });
        break;
    }
  }

  onMenuOpen() {
    // We don't want to focus the first radio button
    if (this.column.type === 'select') {
      return;
    }

    const input = document.querySelector<HTMLInputElement>(`#${this.menu.panelId} input`);
    if (!input) {
      return;
    }
    input.focus();
  }

  onSubmit() {
    if (!this.column.isFilterable) {
      throw new Error(`Column(${this.column.name}) is not filterable`);
    }

    if (!this.hasValue) {
      this.clear();
      return;
    }

    this.trigger.closeMenu();

    switch (this.column.type) {
      case 'number':
      case 'text': {
        const value = this.form.value.value;
        this.filter.emit({ __type: 'text', value });
        break;
      }
      case 'exact_datetime': {
        const from = moment(this.form.value.from, moment.ISO_8601, true);
        const to = moment(this.form.value.to, moment.ISO_8601, true);
        this.filter.emit({ __type: 'date', from, to });
        break;
      }
      case 'date': {
        const from = this.form.value.from;
        const to = this.form.value.to;
        this.filter.emit({ __type: 'date', from, to });
        break;
      }
      case 'select': {
        const value = this.form.value.value;
        this.filter.emit({ __type: 'select', value });
        break;
      }
      case 'multi-select': {
        const values = this.form.value.value;
        this.filter.emit({ __type: 'multi-select', values });
        break;
      }
      case 'boomer_user':
      case 'agent': {
        const value = this.form.value.value;
        this.filter.emit({ __type: 'agent', value });
      }
    }
  }

  clear() {
    switch (this.column.type) {
      case 'date':
      case 'exact_datetime':
        this.form.patchValue({ from: null, to: null }, { emitEvent: false });
        break;
      case 'text':
      case 'number':
      case 'select':
      case 'agent':
      case 'boomer_user':
        this.form.patchValue({ value: null }, { emitEvent: false });
        break;
      case 'multi-select':
        this.form.patchValue({ value: [] }, { emitEvent: false });
        break;
    }

    this.trigger.closeMenu();
    this.filter.emit(null);
  }
}
